/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
    IAgentPlugin,
    IPluginMethodMap,
  } from "@vlinder-web/plugin-agent-service-react";
  import axios from "axios";
  import _ from "lodash";
  import { toHeaderCase } from "js-convert-case";
  import moment from "moment";
  
  import config from "../config/credentialDetail.config.json";
  
  import { useCredId } from "../provider";
  
  import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
  import { DRAFT_CREDENTIAL_DETAIL } from "../graphql";
  import { VApplication } from "../../../application";
  import { AppNavigatorService } from "../../appNavigatorService";
  
  const getGraphQLModule = () => {
    const app = VApplication.getInstance();
    return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
  };
  
  const getWhoamiSrv = () => {
    const app = VApplication.getInstance();
    return app.getSync("services.AppNavigatorService") as AppNavigatorService;
  };
  
  export interface IDraftCredentialDetailMethods extends IPluginMethodMap {
    getDraftCredentialDetailProps(args: any): Promise<any>;
  }
  
  function isValidHttpUrl(string: string) {
    let url;
  
    try {
      url = new URL(string);
    } catch (_) {
      return false;
    }
  
    return url.protocol === "http:" || url.protocol === "https:";
  }
  
  export class DraftCredentialDetailService implements IAgentPlugin {
    readonly methods: IDraftCredentialDetailMethods;
  
    constructor() {
      this.methods = {
        getDraftCredentialDetailProps: this.getDraftCredentialDetailProps.bind(this),
      };
      
    }
  
    async getDraftCredentialDetailProps(args: any) {
      const graphqlModule = getGraphQLModule();
      const client = graphqlModule.getClient("graphql-gateway");
      const { credId } = useCredId();
      try {
        if (client) {
          const res = await client.query({
            query: DRAFT_CREDENTIAL_DETAIL,
            variables: {
              getCredentialDetailId: credId,
            },
            fetchPolicy: "network-only",
          });
          if (res?.data) {
            return {
              data: res?.data?.getCredentialDetail,
              isWalletRegistered:this.getWalletRegistered(res?.data?.getCredentialDetail),
              imageItems: this.formatImages(res?.data?.getCredentialDetail?.image),
              logoSrc: res?.data?.getCredentialDetail?.logo,
              pdfFile: res?.data?.getCredentialDetail?.pdf,
              title: this.formatTitle(res?.data?.getCredentialDetail),
              badgeTitle: res?.data?.getCredentialDetail?.status,
              badgeTitleBg: this.formatBadgeTitleBg(res?.data?.getCredentialDetail),
              subtitle: this.formatSubTitle(res?.data?.getCredentialDetail),
              description: res?.data?.getCredentialDetail?.description,
              credential: this.formatAttributes(res?.data?.getCredentialDetail),
              buttons: await this.formatProductActions(
                res?.data?.getCredentialDetail
              ),
              attributeItems: this.formatAttributeItems(
                res?.data?.getCredentialDetail
              ),
              tableItems: this.formatDigitalSignatures(
                res?.data?.getCredentialDetail
              ),
              carouselItems: this.formatCarouselItems(
                res?.data?.getCredentialDetail
              ),
              listItemTitle: this.formatGetListItemTitle(),
            };
          }
        }
      } catch (err: any) {
        return err;
      }
    }
  
    private async formatProductActions(credential?: any) {
      const serv = getWhoamiSrv();
      let user = await serv?.whoAmI();
      const formatted: any = [];
      if (credential?.status === "ACTIVE" && (user?.whoami === "admin" || user?.whoami === "master admin") ) {
        formatted.push(config?.revokeCertificate);
        if(credential?.holderStatus!=="Removed"){
          formatted.push(config?.resendCredential);
        }      
      }
      return formatted;
    }
  
    private getWalletRegistered(credential:any){
      let isWalletRegistered = false;
  
      if(credential?.accountStatus === "WALLET_REGISTERED"){
        isWalletRegistered = true;
      }
      
      return isWalletRegistered;
    }
  
    private formatAttributes(credential?: any) {
      let _attributeData: any = [];
      let formatted: any = { ...(credential?.credentialSubject || {}) };
      if (formatted) {
        for (let key in formatted) {
          if (config?.credentialSubject?.blacklistedKeys?.includes(key)) {
            delete formatted[key];
          }
        }
      }
  
      Object.entries(formatted).forEach(([key, value]) => {
        if(typeof(value)==="string"){
          _attributeData.push({
            label: toHeaderCase(key ?? ""),
            value: value,
          });
        }        
      });
      return _attributeData;
    }
  
    private formatBadgeTitleBg(credential?: any) {
      let formatted: any = "danger";
      if (credential?.status?.toLowerCase()?.includes("active")) {
        formatted = "success";
      }
      return formatted;
    }
  
    private formatTitle(credential: any) {
      let formatted = credential?.credentialName ?? "";
      return toHeaderCase(formatted);
    }
  
    private formatSubTitle(credential: any) {
      let formatted = credential?.issuer ?? "";
      return toHeaderCase(formatted);
    }
  
    private formatImages(image?: string) {
      const formatted: any = [];
      if (image) {
        formatted.push({
          original: `data:image/png;base64,${image}`,
          thumbnail: `data:image/png;base64,${image}`,
        });
      }
      return formatted;
    }
  
    private formatGetListItemTitle() {
      return config?.digitalSignatures?.title;
    }
  
    private formatAttributeItems(credential: any) {
      const formatted: any = {
        title: config?.digitalSignatures?.title,
        info: config?.attributeItems,
      };
      if (credential) {
        for (let credKey in credential) {
          if (credKey === "issuer") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "digitalSignedBy"
            );
            if (index > -1) {
              formatted.info[index].value = credential[credKey];
            }
          } else if (credKey === "issuerDid") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "signerDID"
            );
            if (index > -1) {
              formatted.info[index].value = credential[credKey];
            }
          } else if (credKey === "issuerPk") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "signerPublicKey"
            );
            if (index > -1) {
              formatted.info[index].value = credential[credKey];
            }
          } else if (credKey === "validFrom") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "validFrom"
            );
            if (index > -1) {
              formatted.info[index].value = this.formatFromDate(credential[credKey] ?? null);
            }
          } else if (credKey === "validUntil") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "validUntil"
            );
            if (index > -1) {
              formatted.info[index].value = this.formatDate(credential[credKey] ?? null);
            }
          } else if (credKey === "status") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "status"
            );
            if (index > -1) {
              formatted.info[index].value = credential[credKey];
            }
          } else {
          }
        }
      }
      return formatted?.info;
    }
  
    private formatDigitalSignatures(credential: any) {
      const formatted: any = {
        title: config?.digitalSignatures?.title,
        info: config?.digitalSignatures?.info,
      };
      if (credential) {
        for (let credKey in credential) {
          if (credKey === "issuer") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "digitalSignedBy"
            );
            if (index > -1) {
              formatted.info[index].value = credential[credKey];
            }
          } else if (credKey === "issuerDid") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "signerDID"
            );
            if (index > -1) {
              formatted.info[index].value = credential[credKey];
            }
          } else if (credKey === "issuerPk") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "signerPublicKey"
            );
            if (index > -1) {
              formatted.info[index].value = credential[credKey];
            }
          } else if (credKey === "validFrom") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "validFrom"
            );
            if (index > -1) {
              formatted.info[index].value = this.formatFromDate(credential[credKey]?? null);
            }
          } else if (credKey === "validUntil") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "validUntil"
            );
            console.log(`find index`, index);
            if (index > -1) {
              formatted.info[index].value = this.formatDate(credential[credKey] ?? null);
            }
          } else if (credKey === "status") {
            const index = _.findIndex(
              formatted.info,
              (info: any) => info.id === "status"
            );
            if (index > -1) {
              formatted.info[index].value = credential[credKey];
            }
          } else {
          }
        }
      }
      return formatted?.info;
    }
  
    private formatDate(date: string) {
      if (date) {
        return moment(date).format("lll");
      } else {
        return "Perpetual";
      }
    }
  
    private formatFromDate(date:string){
      if(date){
        return moment(date).format("lll");
      } else {
        return "-"
      }
    }
  
    private formatCarouselItems(credential: any) {
      let _docs: any = [];
      if (credential?.pdf) {
        const src = isValidHttpUrl(credential?.pdf)
          ? credential?.pdf
          : `data:application/pdf;base64,${credential?.pdf}`;
        _docs.push({
          type: "pdf",
          poster: credential?.logo,
          src: src,
          title: credential?.credentialName,
        });
      }
      Array.isArray(credential?.evidence) &&
        credential?.evidence.map((item: any) => {
          let _type =item?.evidenceDocumentType?.split("/");
          _docs.push({
            type: _type[0] ==="image" ? "img": _type[0] ==="video" ? "video" : _type[0] ==="audio" ? "audio": "pdf", 
            poster: item?.poster,
            src: item?.id,
            title: item?.evidenceDocument?.replace(/\.[^/.]+$/, ""),
          });
        });
      return _docs;
    }
  }
  